(ns app.front.view.inspections.event-list
  (:require [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.file.download :as file-download]
            [app.front.ui.chevron :as c]
            [app.front.ui.notes :as ui-notes]
            [app.front.ui.separator :as ui-sep]
            [app.front.util :as futil]
            [app.front.view.inspections.booking-dialog :as booking-dialog]
            [app.front.view.inspections.time-tracking :as time-tracking]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def status->class {:booking.state/to-dispatch "border-gray-600"
                    :booking.state/dispatched  "border-success"
                    :booking.state/cancelled   "border-gray-600"})


(def status-type->class
  {:inspection.visit.result-status-type/in-progress "border-ui-dark-gray"
   :inspection.visit.result-status-type/passed      "border-success"
   :inspection.visit.result-status-type/failed      "border-fail"
   :inspection.visit.result-status-type/completed   "border-ui-black"})

(def result->class
  {:inspection.visit.result/failed       "border-fail"
   :inspection.visit.result/passed       "border-success"
   :inspection.visit.result/not-complete "border-ui-dark-gray"
   :inspection.visit.result/no-access    "border-ui-dark-gray"})

(rf/reg-sub ::visits
  :<- [::entities/context]
  (fn [ctx [_ inspection-id]]
    (entities/find-entities ctx
                            {:entity/parent inspection-id
                             :entity/type   :entity.type/inspection-visit})))


(rf/reg-sub ::bookings
  :<- [::entities/entities {:entity/type :entity.type/booking}]
  (fn [bookings [_ inspection-id]]
    (->> bookings
         (filter #(and (-> % :entity/parent :db/id (= inspection-id))
                       (-> % :inspection.visit/_booking empty?))))))


(rf/reg-sub ::actions
  (fn [db [_ inspection-id]]
    (-> db ::inspection-actions (get inspection-id))))


(rf/reg-sub ::inspection-items
  ;; visits, bookings and actions
  (fn [[_ inspection-id]]
    [(rf/subscribe [::visits inspection-id])
     (rf/subscribe [::bookings inspection-id])
     (rf/subscribe [::actions inspection-id])])
  (fn [[visits bookings actions] _]
    ;; display fulfilled bookings as in progress visits to users who are not allowed to view in progress visits
    (let [in-progress-or-done? (some (du/in-state-fn :inspection.visit.state/in-progress :inspection.visit.state/done) visits)
          displayed-bookings   (if in-progress-or-done?
                                 (remove (du/in-state-fn :booking.state/fulfilled) bookings)
                                 bookings)]
      (->> (concat visits displayed-bookings actions)
           (sort-by #(or (:booking/requested-start-time %)
                         (:inspection.visit/start-time %)
                         (:db/txInstant %))
                    >)
           (util/map-indexed-pred vector :entity/type))))) ;; Actions are not numbered


(defn render-status-row [border-color-class & children]
  (into
    [:div.p-1.pl-3.flex.flex-wrap.flex-1.justify-between.items-center.bg-white.border-l-4.border-solid
     {:class border-color-class}]
    children))


(defn booking-or-visit [{:keys [tr
                                index-class
                                status-class]}
                        index
                        status-description
                        date-interval-str
                        date-requested-str
                        person
                        requester
                        action-button
                        expand-content]
  (r/with-let [*expanded? (r/atom false)]
    (let [all-visits-requestor-enabled? (<sub [::ff/enabled? :feature-flag.id/all-visits-inspection-requester])
          show-requested-date?          (<sub [::ff/enabled? :feature-flag.id/requested-date-on-bookings])
          requested-msg                 (if date-requested-str
                                          (str (tr [:booking/requested-on] [date-requested-str]))
                                          (tr [:booking/request-date-not-available]))]
      [:div.flex.mb-2.w-full
       [:div.p-1.w-10.flex.items-center {:class index-class} index]
       [render-status-row status-class
        [:div.flex.w-full.flex-col.sm:flex-row.flex-wrap.md:flex-nowrap.gap-4
         [(if show-requested-date?
            :div.flex.items-center.uppercase
            :div.flex.items-center.uppercase.w-full)
          (when expand-content
            [:button.mr-2.border.border-gray-800.bg-transparent.text-sm.font-bold
             {:on-click #(swap! *expanded? not)}
             [c/chevron {:tr        tr
                         :side      :left
                         :expanded? @*expanded?
                         :class     "inspection__chevron"}]])
          status-description]
         (when show-requested-date?
           [:div.flex.w-full.items-center requested-msg])
         [:div {:class "w-full sm:w-1/2 md:w-1/4 py-2 text-left md:text-right pr-4"} date-interval-str]
         [:div {:class "w-full sm:w-1/2 md:w-1/4 my-1 pr-4"} person]
         (when (and all-visits-requestor-enabled? requester)
           [:div {:class "w-full sm:w-1/2 md:w-1/4 my-1 md:block hidden"} requester])
         [:div {:class "w-full sm:w-1/2 md:w-1/4 my-1 text-right"}
          action-button]]
        (when @*expanded?
          expand-content)]])))

(defn booking-expand-content [tr {notes :booking/notes}]
  [:div
   [:h4.mt-2.font-bold.text-sm (tr [:booking/notes])]
   [:p.pb-2.leading-normal notes]])


(defn person-name [{:keys [tr]} person]
  (if person
    [:span
     (str (user-model/full-name person) " - (" (tr [:inspection/inspector]) ")")]
    (tr [:booking/not-assigned])))

(defn requester-name [{:keys [tr]} requester]
  (if requester
    [:span
     (str (user-model/full-name requester) " - (" (tr [:role/requester]) ")")]
    ""))


(defn render-edit-button [booking tr]
  (let [target (-> booking
                   :entity/parent ;inspection
                   :entity/parent ;application or execution
                   )]
    (when (<sub [:app.front.process/signal-permitted?
                 target
                 [:process.signal/update-booking {:booking-id (:db/id booking)}]])
      (cc/button
        {:kind             :tertiary
         :size             :small
         :class-name       "my-0 mr-1"
         :on-click         (fn [] (>evt [::booking-dialog/edit-booking booking]))
         :icon             i/pen
         :icon-description ""}
        (tr [:booking/edit])))))


(defmulti inspection-item
  "Renders a single booking or visit row, expandable to reveal more info"
  (fn [_ _ {type :entity/type}] (or type :action)))

(defmethod inspection-item :default [_ _ _] [:div.coming-soon])

(defmethod inspection-item :entity.type/booking
  [{:keys [tr latest? group-tz]} index booking]
  (let [start-time         (or (:booking/actual-start-time booking)
                               (:booking/requested-start-time booking))
        end-time           (or (:booking/actual-end-time booking)
                               (:booking/requested-end-time booking))
        interval-str       (date-util/interval-in-tz start-time end-time group-tz)
        requester          (:entity/creator booking)
        requested-str      (some-> booking :entity/created (date-util/date-time-in-tz group-tz))
        status             (:domain/state booking)
        status-description (if (= status :booking.state/fulfilled)
                             (tr [:inspection.visit/in-progress])
                             (tr [(keyword "booking-status" (name status))]))
        index-classes      (when latest? "font-bold")]
    [booking-or-visit {:tr           tr
                       :index-class  index-classes
                       :status-class (status->class status)}
     index
     status-description
     interval-str
     requested-str
     [person-name {:tr tr} (:booking/assignee booking)]
     [requester-name {:tr tr} requester]
     [render-edit-button booking tr]
     (when (:booking/notes booking)
       [booking-expand-content tr booking])]))

(defmethod inspection-item :entity.type/inspection-visit
  [{:keys [tr latest? group-tz show-visit-files?] :as props}
   index
   {:inspection.visit/keys [visit-result result failure-reason occupancy-result start-time inspectors result-details booking]
    :domain/keys           [state]
    :as                    visit}]
  (let [ongoing?              (= state :inspection.visit.state/in-progress)
        save-value            (partial (ee/save-fn :process.signal/update-previous-inspection-visit)
                                       (-> visit :entity/parent :entity/parent)
                                       (:db/id visit)
                                       :inspection.visit/time-spent)
        inspection-visit-file (inspection-model/inspection-visit-file visit)
        lang                  (<sub [:language])
        requester             (:entity/creator booking)
        requested-str         (some-> booking :entity/created (date-util/date-time-in-tz group-tz))]
    [booking-or-visit
     {:tr           tr
      :status-class (or (some-> visit-result :inspection-visit-result/status-type status-type->class)
                        (result->class result)) ;; remove when :feature-flag.id/inspection-visit-configuration removed
      :index-class  (when (and latest? ongoing?) "font-bold")}
     index
     [:div.flex.flex-row.flex-wrap
      [:span.mr-2 (if ongoing?
                    (tr [:inspection.visit/in-progress])
                    (or (some-> visit-result (inspection-model/visit-result-name lang))
                        (tr [(or occupancy-result ;; remove when :feature-flag.id/inspection-visit-configuration removed
                                 result)])))]
      (when (and (not ongoing?)
                 (or failure-reason (seq result-details)))
        [:span.font-bold
         (cond
           (seq result-details)
           (->> result-details
                (map #(inspection-model/visit-result-detail-name % lang))
                (str/join ", "))
           failure-reason ;; remove when :feature-flag.id/inspection-visit-configuration removed
           (tr [failure-reason]))])]
     (date-util/date-time-in-tz start-time group-tz)
     requested-str
     (->> inspectors
          (map (fn [inspector]
                 [person-name props inspector]))
          (interpose [:span ", "])
          (into [:<>]))
     [requester-name {:tr tr} requester]
     (when (and show-visit-files?
                (:db/id inspection-visit-file))
       [file-download/download-workspace-file-link
        inspection-visit-file
        {:text (if (:inspection.visit/occupancy-permit-file visit)
                 (tr [:inspection/download-occupancy-permit-title])
                 (tr [:inspection/download-inspection-report-title]))}])
     (when (not ongoing?)
       [:div.w-full
        [:div.my-2 (tr [(:inspection.visit/type visit)])]
        [ui-notes/internal-external-note
         {:tr             tr
          :internal       (:inspection.visit/internal-notes visit)
          :internal-title (tr [:inspection.visit/internal-notes])
          :external       (:inspection.visit/notes visit)
          :external-title (tr [:inspection.visit/notes])}]
        (when (<sub [::time-tracking/enabled?])
          [time-tracking/time-tracking {:tr        tr
                                        :value     (<sub [::ee/attr-value
                                                          (:db/id visit)
                                                          :inspection.visit/time-spent])
                                        :on-change save-value}])])]))

(defmethod inspection-item :action
  [{:keys [tr group-tz separator-opts]} _ action]
  (let [user (<sub [::entities/entity (:audit/user action)])]
    [:div.my-3.flex
     [:div.flex.w-10]
     [:div.flex.w-full
      [ui-sep/horizontal
       (merge {:label-text (str (str/upper-case (tr [:inspection/reopened]))
                                " ("
                                (user-model/full-name user)
                                ", "
                                (date-util/date-time-in-tz (:db/txInstant action) group-tz)
                                "): "
                                (:audit/reason action))}
              separator-opts)]]]))


(defn inspection-items
  "Render events for an inspection - bookings, visits and actions

  Remember to also include `booking-dialog/render-booking-modal`
  somewhere"
  [props {inspection-id :db/id}]
  (let [items    (<sub [::inspection-items inspection-id])
        group-tz (<sub [::ws-subs/group-time-zone])
        latest   (->> items (map first) (apply max) inc)]
    (futil/for-frag [[i item] items]
      (let [index (- latest i)
            props (-> props
                      (assoc :latest? (= latest index)
                             :group-tz group-tz))]
        [inspection-item props index item]))))
